'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Experience {
  id: number;
  title: string;
  host: string;
  hostAvatar: string;
  location: string;
  country: string;
  category: string;
  duration: string;
  groupSize: string;
  price: number;
  currency: string;
  rating: number;
  reviews: number;
  image: string;
  alt: string;
  tag?: string;
  tagColor?: string;
  languages: string[];
  highlights: string[];
  isSuperhost?: boolean;
}

const experiences: Experience[] = [
  {
    id: 1,
    title: 'Sunrise Gorilla Trekking in Bwindi',
    host: 'Samuel Kato',
    hostAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&q=80',
    location: 'Bwindi',
    country: 'Uganda',
    category: 'wildlife',
    duration: '6 hours',
    groupSize: 'Up to 8',
    price: 120,
    currency: 'USD',
    rating: 4.98,
    reviews: 214,
    image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&q=80',
    alt: 'Mountain gorilla family in the misty Bwindi Impenetrable Forest at sunrise',
    tag: 'Bestseller',
    tagColor: 'bg-[#4088C6]',
    languages: ['English', 'Luganda'],
    highlights: ['Expert ranger guide', 'Gorilla permit included', 'Forest breakfast'],
    isSuperhost: true,
  },
  {
    id: 2,
    title: 'White Water Rafting on the Nile',
    host: 'Amara Osei',
    hostAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&q=80',
    location: 'Jinja',
    country: 'Uganda',
    category: 'adventure',
    duration: '4 hours',
    groupSize: 'Up to 12',
    price: 75,
    currency: 'USD',
    rating: 4.95,
    reviews: 389,
    image: 'https://images.unsplash.com/photo-1530866495561-507c9faab2ed?w=800&q=80',
    alt: 'Rafters navigating powerful white water rapids on the Nile River in Jinja',
    tag: 'Top rated',
    tagColor: 'bg-blue-500',
    languages: ['English'],
    highlights: ['Grade 5 rapids', 'Safety briefing', 'Lunch included'],
    isSuperhost: true,
  },
  {
    id: 3,
    title: 'Traditional Ugandan Cooking Class',
    host: 'Grace Namukasa',
    hostAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&q=80',
    location: 'Kampala',
    country: 'Uganda',
    category: 'food',
    duration: '3 hours',
    groupSize: 'Up to 6',
    price: 45,
    currency: 'USD',
    rating: 4.92,
    reviews: 156,
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80',
    alt: 'Hands-on traditional Ugandan cooking class with local ingredients and spices',
    tag: 'New',
    tagColor: 'bg-green-500',
    languages: ['English', 'Luganda'],
    highlights: ['Market visit', 'Cook 5 dishes', 'Recipe booklet'],
    isSuperhost: false,
  },
  {
    id: 4,
    title: 'Sunset Kayaking on Lake Victoria',
    host: 'David Mugisha',
    hostAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&q=80',
    location: 'Entebbe',
    country: 'Uganda',
    category: 'water',
    duration: '2.5 hours',
    groupSize: 'Up to 10',
    price: 55,
    currency: 'USD',
    rating: 4.89,
    reviews: 98,
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
    alt: 'Kayakers paddling on Lake Victoria during a golden sunset with reflections on the water',
    languages: ['English', 'French'],
    highlights: ['Equipment provided', 'Sunset views', 'Snacks on board'],
    isSuperhost: false,
  },
  {
    id: 5,
    title: 'Kampala Street Art & Culture Walk',
    host: 'Fatima Nakato',
    hostAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80',
    location: 'Kampala',
    country: 'Uganda',
    category: 'culture',
    duration: '3 hours',
    groupSize: 'Up to 15',
    price: 30,
    currency: 'USD',
    rating: 4.87,
    reviews: 203,
    image: 'https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=800&q=80',
    alt: 'Vibrant street murals and local art installations in the heart of Kampala city',
    tag: 'Popular',
    tagColor: 'bg-purple-500',
    languages: ['English'],
    highlights: ['Local artist meetup', 'Hidden murals', 'Coffee tasting'],
    isSuperhost: true,
  },
  {
    id: 6,
    title: 'Chimpanzee Tracking in Kibale Forest',
    host: 'Robert Byaruhanga',
    hostAvatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&q=80',
    location: 'Kibale',
    country: 'Uganda',
    category: 'wildlife',
    duration: '5 hours',
    groupSize: 'Up to 6',
    price: 95,
    currency: 'USD',
    rating: 4.96,
    reviews: 142,
    image: 'https://images.unsplash.com/photo-1564349683136-77e08dba1ef7?w=800&q=80',
    alt: 'Chimpanzee swinging through the dense canopy of Kibale Forest in Uganda',
    tag: 'Rare find',
    tagColor: 'bg-amber-500',
    languages: ['English'],
    highlights: ['Chimp permit', 'Forest walk', 'Bird watching'],
    isSuperhost: true,
  },
  {
    id: 7,
    title: 'Boda Boda City Tour with Local Guide',
    host: 'Moses Ssekandi',
    hostAvatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=80&q=80',
    location: 'Kampala',
    country: 'Uganda',
    category: 'culture',
    duration: '4 hours',
    groupSize: 'Up to 4',
    price: 35,
    currency: 'USD',
    rating: 4.83,
    reviews: 87,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
    alt: 'Motorcycle boda boda weaving through the colourful streets of Kampala with a local guide',
    languages: ['English', 'Luganda'],
    highlights: ['Local markets', 'Street food stops', 'Photo spots'],
    isSuperhost: false,
  },
  {
    id: 8,
    title: 'Murchison Falls Boat Safari',
    host: 'Christine Akello',
    hostAvatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=80&q=80',
    location: 'Murchison Falls',
    country: 'Uganda',
    category: 'wildlife',
    duration: '3 hours',
    groupSize: 'Up to 20',
    price: 65,
    currency: 'USD',
    rating: 4.94,
    reviews: 178,
    image: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=800&q=80',
    alt: 'Boat safari on the Nile approaching the thundering Murchison Falls with hippos nearby',
    tag: 'Bestseller',
    tagColor: 'bg-[#4088C6]',
    languages: ['English'],
    highlights: ['Hippo sightings', 'Nile crocodiles', 'Falls view'],
    isSuperhost: true,
  },
];

const categories = [
  { id: 'all', label: 'All Experiences', icon: '✨' },
  { id: 'wildlife', label: 'Wildlife', icon: '🦍' },
  { id: 'adventure', label: 'Adventure', icon: '🏄' },
  { id: 'food', label: 'Food & Drink', icon: '🍽️' },
  { id: 'water', label: 'Water Sports', icon: '🚣' },
  { id: 'culture', label: 'Culture & Art', icon: '🎨' },
];

export default function ExperiencesPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const filtered = experiences.filter((e) => {
    const matchCat = activeCategory === 'all' || e.category === activeCategory;
    const matchSearch =
      !search ||
      e.title.toLowerCase().includes(search.toLowerCase()) ||
      e.location.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <div
        ref={heroRef}
        className="relative h-[420px] md:h-[500px] overflow-hidden"
      >
        <AppImage
          src="https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1600&q=80"
          alt="Lush Ugandan landscape with wildlife and adventure experiences waiting to be discovered"
          fill
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        <div className={`absolute inset-0 flex flex-col items-center justify-center px-4 text-center transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block bg-[#4088C6] text-white text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-wide uppercase">
            Discover Uganda
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight max-w-3xl">
            Unforgettable<br />
            <span className="text-[#F5A623]">Experiences</span>
          </h1>
          <p className="text-white/80 text-lg max-w-xl mb-8">
            Handpicked adventures, cultural immersions, and local encounters — hosted by passionate locals.
          </p>
          {/* Search */}
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl flex items-center overflow-hidden">
            <svg className="w-5 h-5 text-gray-400 ml-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search experiences or locations…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-4 py-4 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent"
            />
            <button className="m-2 bg-[#4088C6] hover:bg-[#3070a6] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Category filter */}
      <div className="sticky top-[64px] z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 overflow-x-auto py-3 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                  activeCategory === cat.id
                    ? 'bg-gray-900 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span>{cat.icon}</span>
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {filtered.length} experience{filtered.length !== 1 ? 's' : ''} available
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Hosted by verified local experts</p>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🔍</div>
            <p className="text-gray-500 text-lg">No experiences found. Try a different search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((exp, i) => (
              <Link
                key={exp.id}
                href={`/experiences/${exp.id}`}
                className={`group block transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="relative h-56 rounded-2xl overflow-hidden mb-3">
                  <AppImage
                    src={exp.image}
                    alt={exp.alt}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  {exp.tag && (
                    <div className={`absolute top-3 left-3 ${exp.tagColor} text-white text-xs font-bold px-2.5 py-1 rounded-full`}>
                      {exp.tag}
                    </div>
                  )}
                  {exp.isSuperhost && (
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-bold px-2 py-1 rounded-full">
                      Superhost
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
                    <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-white flex-shrink-0">
                      <AppImage src={exp.hostAvatar} alt={`${exp.host} profile photo`} width={28} height={28} className="object-cover" />
                    </div>
                    <span className="text-white text-xs font-semibold drop-shadow">{exp.host}</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 leading-snug group-hover:text-[#4088C6] transition-colors line-clamp-2">
                      {exp.title}
                    </h3>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-amber-400 fill-amber-400" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="text-xs font-semibold text-gray-800">{exp.rating}</span>
                      <span className="text-xs text-gray-400">({exp.reviews})</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{exp.location}, {exp.country}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {exp.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {exp.groupSize}
                    </span>
                  </div>
                  <p className="text-sm font-bold text-gray-900 mt-2">
                    <span className="text-[#4088C6]">${exp.price}</span>
                    <span className="text-gray-400 font-normal text-xs"> / person</span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
